import { useEffect, useState } from 'react';
import type {
  DocumentStatus,
  DocumentSummary,
  PublicUser,
  SignatureEventView,
  SignerSummary,
} from '@otter/shared';
import { api, apiForm, downloadFile } from '../lib/api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FieldDesigner } from '../components/FieldDesigner';
import { fmtDateTime } from '../lib/format';
import { hasPermission } from '../lib/perm';

type Props = {
  user: PublicUser;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

const STATUS_PILL: Record<DocumentStatus, { className: string; label: string }> = {
  draft: { className: 'pill draft', label: 'BORRADOR' },
  sent: { className: 'pill sent', label: 'ENVIADO' },
  viewed: { className: 'pill info', label: 'VISTO' },
  signed: { className: 'pill ok', label: 'FIRMADO' },
  expired: { className: 'pill warn', label: 'CADUCADO' },
};

const EVENT_LABEL: Record<SignatureEventView['eventType'], string> = {
  sent: 'Enviado',
  opened: 'Abierto',
  viewed: 'Documento visto',
  otp_requested: 'OTP solicitado',
  otp_validated: 'OTP validado',
  signed: 'Firmado',
};

function StatusPill({ status }: { status: DocumentStatus }) {
  const def = STATUS_PILL[status];
  return <span className={def.className}>{def.label}</span>;
}

export function Documentos({ user, onToast, onError }: Props) {
  const [items, setItems] = useState<DocumentSummary[]>([]);
  const [show, setShow] = useState(false);
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  const [delDoc, setDelDoc] = useState<DocumentSummary | null>(null);
  const [designDoc, setDesignDoc] = useState<DocumentSummary | null>(null);

  const canManage = hasPermission(user, 'signatures.manage');

  async function remove(doc: DocumentSummary) {
    try {
      await api(`/admin/documents/${doc.id}`, { method: 'DELETE' });
      setDelDoc(null);
      await load();
      onToast('Documento eliminado', 'ok');
    } catch (e) {
      onError(e);
    }
  }

  async function send(doc: DocumentSummary) {
    try {
      await api(`/admin/documents/${doc.id}/send`, { method: 'POST' });
      await load();
      onToast('Documento enviado a firma', 'ok');
    } catch (e) {
      onError(e);
    }
  }

  async function load() {
    try {
      const res = await api<{ items: DocumentSummary[] }>('/admin/documents');
      setItems(res.items);
    } catch (e) {
      onError(e);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="body">
      <div className="pageh">
        <h1>Firmas</h1>
        <div className="sub">
          {items.length} documento{items.length === 1 ? '' : 's'}
        </div>
        <div className="actions">
          <button className="btn primary" onClick={() => setShow(true)}>
            <span className="plus">+</span> Nuevo documento
          </button>
        </div>
      </div>

      <div className="card">
        <table className="t">
          <thead>
            <tr>
              <th>Documento</th>
              <th>Estado</th>
              <th>Firmantes</th>
              <th>Creado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((d) => {
              const signed = d.signers.filter((s) => s.signedAt).length;
              return (
                <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setOpenDoc(d.id)}>
                  <td data-label="Documento">
                    <b>{d.filename}</b>
                  </td>
                  <td data-label="Estado">
                    <StatusPill status={d.status} />
                  </td>
                  <td data-label="Firmantes" className="mono">
                    {signed}/{d.signers.length}
                  </td>
                  <td data-label="Creado" className="mono">
                    {fmtDateTime(d.createdAt)}
                  </td>
                  <td className="num">
                    {d.status === 'signed' && (
                      <button
                        className="btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          void downloadFile(`/admin/documents/${d.id}/signed`, d.filename);
                        }}
                      >
                        Descargar sellado
                      </button>
                    )}
                    {canManage && d.status === 'draft' && (
                      <button
                        className="btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDesignDoc(d);
                        }}
                      >
                        Diseñar
                      </button>
                    )}
                    {canManage && d.status === 'draft' && (
                      <button
                        className="btn primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          void send(d);
                        }}
                      >
                        Enviar
                      </button>
                    )}
                    {canManage && (
                      <button
                        className="btn danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDelDoc(d);
                        }}
                      >
                        Borrar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && <div className="empty">sin documentos</div>}
      </div>

      {show && (
        <CreateDocumentModal
          onClose={() => setShow(false)}
          onCreated={() => {
            setShow(false);
            void load();
            onToast('Borrador creado', 'ok');
          }}
          onError={onError}
        />
      )}

      {openDoc && (
        <DocumentDetailModal
          documentId={openDoc}
          canManage={canManage}
          onClose={() => setOpenDoc(null)}
          onToast={onToast}
          onError={onError}
          onChanged={load}
        />
      )}

      {designDoc && (
        <FieldDesigner
          documentId={designDoc.id}
          signers={designDoc.signers.map((s) => ({ id: s.id, fullName: s.fullName }))}
          onClose={() => setDesignDoc(null)}
          onSaved={() => {
            setDesignDoc(null);
            onToast('Campos guardados', 'ok');
          }}
          onError={onError}
        />
      )}

      {delDoc && (
        <ConfirmDialog
          title="Eliminar documento"
          message={`Se borrará "${delDoc.filename}", sus firmantes, el rastro de auditoría y los PDFs. Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          tone="danger"
          onConfirm={() => remove(delDoc)}
          onCancel={() => setDelDoc(null)}
        />
      )}
    </div>
  );
}

type SignerDraft = { fullName: string; email: string; phone: string };

function CreateDocumentModal(props: {
  onClose: () => void;
  onCreated: () => void;
  onError: (e: unknown) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [signers, setSigners] = useState<SignerDraft[]>([{ fullName: '', email: '', phone: '' }]);
  const [busy, setBusy] = useState(false);

  function updateSigner(i: number, patch: Partial<SignerDraft>) {
    setSigners((list) => list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      props.onError(new Error('Selecciona un PDF'));
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append(
        'meta',
        JSON.stringify({
          filename: filename || file.name,
          expiresInDays,
          signers,
        }),
      );
      await apiForm('/admin/documents', form);
      props.onCreated();
    } catch (err) {
      props.onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Nuevo documento</h3>
        <div className="field">
          <label>PDF</label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !filename) setFilename(f.name);
            }}
            required
          />
        </div>
        <div className="field">
          <label>Nombre del documento</label>
          <input value={filename} onChange={(e) => setFilename(e.target.value)} required />
        </div>
        <div className="field">
          <label>Caducidad (días)</label>
          <input
            type="number"
            min={1}
            max={90}
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number.parseInt(e.target.value, 10) || 30)}
          />
        </div>

        <div className="field">
          <label>Firmantes</label>
        </div>
        {signers.map((s, i) => (
          <div key={i} className="field" style={{ borderLeft: '2px solid var(--line)', paddingLeft: 10 }}>
            <input
              placeholder="Nombre completo"
              value={s.fullName}
              onChange={(e) => updateSigner(i, { fullName: e.target.value })}
              required
            />
            <input
              placeholder="email@ejemplo.com"
              type="email"
              value={s.email}
              onChange={(e) => updateSigner(i, { email: e.target.value })}
              required
            />
            <input
              placeholder="+34611222333"
              value={s.phone}
              onChange={(e) => updateSigner(i, { phone: e.target.value })}
              required
            />
            {signers.length > 1 && (
              <button
                type="button"
                className="btn"
                onClick={() => setSigners((list) => list.filter((_, idx) => idx !== i))}
              >
                Quitar firmante
              </button>
            )}
          </div>
        ))}
        {signers.length < 10 && (
          <button
            type="button"
            className="btn"
            onClick={() => setSigners((list) => [...list, { fullName: '', email: '', phone: '' }])}
          >
            + Añadir firmante
          </button>
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={props.onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? 'Creando…' : 'Crear borrador'}
          </button>
        </div>
      </form>
    </div>
  );
}

function DocumentDetailModal(props: {
  documentId: string;
  canManage: boolean;
  onClose: () => void;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
  onChanged: () => Promise<void> | void;
}) {
  const [doc, setDoc] = useState<DocumentSummary | null>(null);
  const [events, setEvents] = useState<SignatureEventView[]>([]);

  async function load() {
    try {
      const res = await api<{ document: DocumentSummary; events: SignatureEventView[] }>(
        `/admin/documents/${props.documentId}`,
      );
      setDoc(res.document);
      setEvents(res.events);
    } catch (e) {
      props.onError(e);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.documentId]);

  async function copyLink(s: SignerSummary) {
    if (!s.signingUrl) return;
    try {
      await navigator.clipboard.writeText(s.signingUrl);
      props.onToast('Enlace copiado', 'ok');
    } catch {
      props.onToast('No se pudo copiar el enlace', 'err');
    }
  }

  async function resend(s: SignerSummary) {
    try {
      await api(`/admin/documents/${props.documentId}/signers/${s.id}/resend`, { method: 'POST' });
      props.onToast('Invitación reenviada', 'ok');
      await load();
      await props.onChanged();
    } catch (e) {
      props.onError(e);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <h3>{doc?.filename ?? 'Documento'}</h3>
        {doc && (
          <>
            <div className="field">
              <label>Estado</label>
              <div>
                <StatusPill status={doc.status} />
              </div>
            </div>

            <div className="field">
              <label>Firmantes</label>
            </div>
            <table className="t">
              <thead>
                <tr>
                  <th>Firmante</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {doc.signers.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <b>{s.fullName}</b>
                      <div className="mono" style={{ color: 'var(--muted)' }}>
                        {s.email}
                      </div>
                    </td>
                    <td>
                      {s.signedAt ? (
                        <span className="pill ok">FIRMADO</span>
                      ) : (
                        <span className="pill sent">PENDIENTE</span>
                      )}
                    </td>
                    <td className="num">
                      {!s.signedAt && props.canManage && (
                        <>
                          <button className="btn" onClick={() => void copyLink(s)}>
                            Copiar enlace
                          </button>{' '}
                          <button className="btn" onClick={() => void resend(s)}>
                            Reenviar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="field">
              <label>Auditoría</label>
            </div>
            <table className="t">
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>IP</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td>{EVENT_LABEL[e.eventType]}</td>
                    <td className="mono">{e.ipAddress ?? '—'}</td>
                    <td className="mono">{fmtDateTime(e.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {events.length === 0 && <div className="empty">sin eventos</div>}
          </>
        )}

        <div className="modal-actions">
          {doc?.status === 'signed' && (
            <button
              className="btn"
              onClick={() => void downloadFile(`/admin/documents/${doc.id}/signed`, doc.filename)}
            >
              Descargar sellado
            </button>
          )}
          <button className="btn primary" onClick={props.onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
